import React, { useEffect, useState } from "react";
import "./AddDep.css";
import { FaEdit, FaPlus } from "react-icons/fa";
import axiosInstance from "../utills/axiosInstance";
import { usePermissions } from "../utills/usePermissions";
import { Alert, Paper } from "@mui/material";

const AddDepartment = () => {
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [showDesigForm, setShowDesigForm] = useState(false);
  const [deptName, setDeptName] = useState("");
  const [desigName, setDesigName] = useState("");
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editingDesigId, setEditingDesigId] = useState(null);

  const { canView: canViewDept, canCreate: canCreateDept, canEdit: canEditDept, canView: canViewDesig, canCreate: canCreateDesig, canEdit: canEditDesig } = usePermissions();

  const serverUrl = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL : window.location.origin;

  const fetchData = async () => {
    try {
      const [deptRes, desigRes] = await Promise.all([
        axiosInstance.get('/api/departments'),
        axiosInstance.get('/api/designations')
      ]);
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
      setError("");
    } catch (err) {
      setError(err.response ? err.response.data.error : err.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [serverUrl]);

  const handleDeptSubmit = async (e) => {
    e.preventDefault();
    if (deptName.trim() === "" || deptName.length > 50) {
      setError("Department name is required and must be 3-50 characters.");
      return;
    }

    try {
      if (editingDeptId) {
        await axiosInstance.put(`/api/departments/${editingDeptId}`, { name: deptName });
        setMessage("Department updated successfully");
      } else {
        await axiosInstance.post('/api/departments', { name: deptName });
        setMessage("Department created successfully");
      }

      setDeptName("");
      setEditingDeptId(null);
      setError("");
      setMessage("");
      setShowDeptForm(false);
      fetchData();
    } catch (err) {
      setError(err.response ? err.response.data.error : err.message);
    }
  };

  const handleDesigSubmit = async (e) => {
    e.preventDefault();
    if (desigName.trim() === "" || desigName.length > 35 || !selectedDeptId) {
      setError("Designation name is required (max 35 chars) and must select a department.");
      return;
    }

    try {
      if (editingDesigId) {
        await axiosInstance.put(`/api/designation/${editingDesigId}`, {
          name: desigName,
          departmentId: selectedDeptId
        });
        setMessage("Designation updated successfully");
      } else {
        await axiosInstance.post('/api/postdesignation', {
          name: desigName,
          departmentId: selectedDeptId
        });
        setMessage("Designation created successfully");
      }

      setDesigName("");
      setSelectedDeptId("");
      setEditingDesigId(null);
      setError("");
      setMessage("");
      setShowDesigForm(false);
      fetchData();
    } catch (err) {
      setError(err.response ? err.response.data.error : err.message);
    }
  };

  const handleEditDept = (dept) => {
    setDeptName(dept.name);
    setEditingDeptId(dept._id);
    setShowDeptForm(true);
  };

  const handleEditDesig = (desig) => {
    setDesigName(desig.name);
    setSelectedDeptId(desig.department._id);
    setEditingDesigId(desig._id);
    setShowDesigForm(true);
  };

  const groupedDesignations = departments?.reduce((acc, dept) => {
    acc[dept._id] = designations.filter(desig => desig.department._id === dept._id);
    return acc;
  }, {});

  if (!canViewDept('department') && !canViewDesig('designation')) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="warning">Insufficient permissions to view Department / Designation.</Alert>
      </Paper>
    );
  }

  return (
    <div className="main-container">
      <h1 className="heading">Department & Designation Management</h1>

      <div className="button-group">
        {canCreateDept('department') && (
          <button
            onClick={() => { setShowDeptForm(!showDeptForm); setShowDesigForm(false); }}
            className="toggle-button"
          >
            {showDeptForm ? <FaEdit /> : <FaPlus />} {showDeptForm ? "Cancel" : "Create Department"}
          </button>
        )}
        {canCreateDesig('designation') && (
          <button
            onClick={() => { setShowDesigForm(!showDesigForm); setShowDeptForm(false); }}
            className="toggle-button"
          >
            {showDesigForm ? <FaEdit /> : <FaPlus />} {showDesigForm ? "Cancel" : "Create Designation"}
          </button>
        )}
      </div>

      {(showDeptForm && (canCreateDept('department') || canEditDept('department'))) && (
        <div className="add-form-container">
          <h3>{editingDeptId ? "Edit Department" : "Add Department"}</h3>
          <form onSubmit={handleDeptSubmit} className="form">
            <div className="form-group">
              <label>Department Name</label>
              <input
                type="text"
                value={deptName}
                onChange={(e) => setDeptName(e.target.value)}
                maxLength={50}
                placeholder="Enter department name"
                required
              />
            </div>
            <button type="submit" className="submit-button">
              {editingDeptId ? "Update" : "Create"}
            </button>
          </form>
        </div>
      )}

      {(showDesigForm && (canCreateDesig('designation') || canEditDesig('designation'))) && (
        <div className="add-form-container">
          <h3>{editingDesigId ? "Edit Designation" : "Add Designation"}</h3>
          <form onSubmit={handleDesigSubmit} className="form">
            <div className="form-group">
              <label>Department</label>
              <select
                value={selectedDeptId}
                onChange={(e) => setSelectedDeptId(e.target.value)}
                required
              >
                <option value="">Select Department</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Designation Name</label>
              <input
                type="text"
                value={desigName}
                onChange={(e) => setDesigName(e.target.value)}
                maxLength={35}
                placeholder="Enter designation name"
                required
              />
            </div>
            <button type="submit" className="submit-button">
              {editingDesigId ? "Update" : "Create"}
            </button>
          </form>
        </div>
      )}

      {message && <div className="alert success">{message}</div>}
      {error && <div className="alert error">{error}</div>}

      <div className="list-container">
        <h3>Departments & Designations</h3>
        {departments.length === 0 ? (
          <p>No departments available.</p>
        ) : (
          <ul className="list">
            {departments.map((department) => (
              <li key={department._id} className="list-item">
                <div className="dept-header">
                  <strong>{department.name}</strong>
                  {canEditDept('department') && (
                    <button onClick={() => handleEditDept(department)} className="edit-button">
                      <FaEdit />
                    </button>
                  )}
                </div>
                <ul className="nested-list">
                  {groupedDesignations[department._id]?.length > 0 ? (
                    groupedDesignations[department._id].map((desig) => (
                      <li key={desig._id} className="nested-item">
                        {desig.name}
                        {canEditDesig('designation') && (
                          <button onClick={() => handleEditDesig(desig)} className="edit-button">
                            <FaEdit />
                          </button>
                        )}
                      </li>
                    ))
                  ) : (
                    <li>No designations yet.</li>
                  )}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AddDepartment;
